import React, { useCallback, useEffect, useMemo, useState } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import cloneDeep from "lodash/cloneDeep";
import styled from "styled-components";
import { SectionHeading } from "../../components/section-heading";
import { Alert, Button, Card } from "react-bootstrap";
import axios from "http/customAxios";
import { FullWidthLoader } from "../../components/full-width-loader";
import InventoryResultRenderer from "../inventory/multiUpdate/inventoryResultRenderer";
import { APP_COLORS } from "../witadmin/Common/constants";
import MultiCheckBoxGroup from "../../../src/modules/witadmin/CRUD/Common/MultiCheckBoxGroup";
import {
  Accommodation,
  GetAccommodationsRequest,
} from "../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_pb";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { AccommodationAPIPromiseClient } from "../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_grpc_web_pb";
import {
  GetAllMealPlansRequest,
  MealPlan,
} from "../../gen/witbooking/backoffice/mealplan/v1/mealplan_api_pb";
import { MealPlanAPIPromiseClient } from "../../gen/witbooking/backoffice/mealplan/v1/mealplan_api_grpc_web_pb";
import { BASE_URL } from "../witadmin/request";
import { OccupationAPIPromiseClient } from "../../gen/witbooking/backoffice/occupation/v1/occupation_api_grpc_web_pb";
import {
  GetAllConfigurationRequest,
  Occupation,
} from "../../gen/witbooking/backoffice/occupation/v1/occupation_api_pb";
import { useLocaleProvider } from "../../components/locale-provider";
import { InventoryAPIPromiseClient } from "../../gen/witbooking/backoffice/inventory/v1/inventory_api_grpc_web_pb";
import {
  CreateInventoryLinesRequest,
  InventoryLineErrorCode,
  InventoryLineValueType,
} from "../../gen/witbooking/backoffice/inventory/v1/inventory_api_pb";
import ArrowDown from "../witadmin/icons/arrowDown";
//@ts-ignore
import { FormGroup } from "react-bootstrap-3";
import { defaultFormMessages } from "../shared/crud/form";

export interface Props {
  propertyTicker: string;
}
interface OperationError {
  message: string;
}

export interface Cancellation {
  propertyTicker: string;
  reservationId: string;
  cancellationResultStatus: string;
}

export interface BatchResponse {
  cancellations: Cancellation[];
}
interface Option {
  ticker: string;
  translations: { locale: string; name: string }[];
}
interface Options {
  accommodations: Option[] | undefined;
  occupations: Option[] | undefined;
  mealPlans: Option[] | undefined;
  conditions: Option[] | undefined;
}

interface SmartFilterOptions {
  adults: boolean;
  babies: boolean;
  children: boolean;
  juniors: boolean;
  seniors: boolean;
}

const messages = {
  ...defaultFormMessages,
  ...defineMessages({
    title: {
      id: "batchInventory.title",
      defaultMessage: "Update inventory",
    },
    accommodations: {
      id: "batchInventory.roomType",
      defaultMessage: "roomType",
    },
    occupations: {
      id: "batchInventory.settings",
      defaultMessage: "settings",
    },
    mealPlans: {
      id: "batchInventory.board",
      defaultMessage: "board",
    },
    conditions: {
      id: "batchInventory.conditions",
      defaultMessage: "conditions",
    },
    create: {
      id: "batchInventory.create",
      defaultMessage: "create",
    },
    saveSuccess: {
      id: "default.saveSuccess",
      defaultMessage: "Saved successfully",
    },
    fetchError: {
      id: "batchInventory.fetchError",
      defaultMessage:
        "Something went wrong. Please try refreshing the page in a few minutes and contact support if the issue persists",
    },
    selectAll: {
      id: "app.entityForm.selectAll",
      description: "Select all",
      defaultMessage: "Select all",
    },
    selectNone: {
      id: "app.entityForm.selectNone",
      description: "Select none",
      defaultMessage: "Select none",
    },
    adults: {
      id: "default.adult",
      description: "Adult",
      defaultMessage: "Adult",
    },
    children: {
      id: "default.child",
      description: "Child",
      defaultMessage: "Child",
    },
    babies: {
      id: "default.baby",
      description: "Baby",
      defaultMessage: "Baby",
    },
    juniors: {
      id: "default.junior",
      description: "Junior",
      defaultMessage: "Junior",
    },
    seniors: {
      id: "default.senior",
      description: "Senior",
      defaultMessage: "Senior",
    },
    guests: {
      id: "default.guests",
      description: "Guests",
      defaultMessage: "Guests",
    },
  }),
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 1rem;
  > * {
    margin: 0 5px;
  }
  :last-child {
    margin-right: 5px;
  }
`;

const ResultContainer = styled.div`
  margin: 16px;
`;

const MessageContainer = styled.span`
  font-weight: 600;
`;

const OptionsTableContainer = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
`;

const StyledMultiCheckBoxGroup = styled(MultiCheckBoxGroup)<{ restrictedHeight: boolean }>`
  ${props => (props.restrictedHeight ? "height: 420px;  overflow: auto;" : "")}
`;

const CheckAllControlFilter = styled.div<{ marginBottom: boolean }>`
  color: ${APP_COLORS.primary};
  cursor: default;
  user-select: none;
  font-size: 12px;
  ${props => (props.marginBottom ? `margin-bottom:10px` : ``)};
  & *:first-child {
    margin-right: 5px;
    cursor: pointer;
  }
  & *:last-child {
    margin-left: 5px;
    cursor: pointer;
  }
`;

const StyledCollapseContainer = styled.span<{ open: boolean }>`
  position: absolute;
  z-index: 2;
  width: 200px;
  background-color: ${({ theme }) => theme.greyBackground};;
  border-radius: 4px;
  border: 1px solid ${props => (props.open ? `#3675b4` : `#dae6e8`)};
  margin-top: -45px;
  padding: 0 5px;
  max-height: ${props => (props.open ? `260px` : `35px`)};
`;

const CollapseHeader = styled.div`
  text-indent: 5px;
  height: 35px;
  word-wrap: break-word;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledArrowDown = styled(ArrowDown)`
  height: 24px;
  width: 24px;
  ${props => (props.flip ? `transform: rotate(0.5turn)` : undefined)};
`;

const CollapseBody = styled.div<{ open: boolean }>`
  display: ${props => (props.open ? `block` : `none`)};
  max-height: 215px;
  height: 100%;
  & *:first-child {
    margin-top: 3px;
  }
`;

const CollapseListWrapper = styled.div`
  overflow: auto;
  max-height: 170px;
  margin-bottom: 5px;
`;

const Placeholder = styled.div`
  min-height: 55px;
`;

function BatchIventory({ propertyTicker }: Props) {
  const accommodationsClient = useGrpcClient(AccommodationAPIPromiseClient);
  const getAccomm = usePropertyGrpcCall(
    accommodationsClient,
    accommodationsClient.getAccommodations
  );
  const mealplansClient = useGrpcClient(MealPlanAPIPromiseClient);
  const occupationsClient = useGrpcClient(OccupationAPIPromiseClient);
  const getOccupationsCall = usePropertyGrpcCall(
    occupationsClient,
    occupationsClient.getAllConfigurations
  );
  const inventoryClient = useGrpcClient(InventoryAPIPromiseClient);
  const createInventoryLinesCall = usePropertyGrpcCall(
    inventoryClient,
    inventoryClient.createInventoryLines
  );

  const locale = useLocaleProvider().currentLocale?.substring(0, 2);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [result, setResult] = useState<any>(undefined);
  const [originalOptions, setOriginalOptions] = useState<Options>();
  const [options, setOptions] = useState<Options>();
  const [values, setValues] = useState<any>({
    occupations: [],
    accommodations: [],
    mealPlans: [],
    conditions: [],
  });
  const [accommodations, setAccommodations] = useState<Accommodation.AsObject[] | undefined>();
  const [mealPlans, setMealplans] = useState<MealPlan.AsObject[] | undefined>();
  const [occupations, setOccupations] = useState<Occupation.AsObject[] | undefined>();
  const [conditions, setConditions] = useState<any[] | undefined>();
  const [smartFilterOpen, setSmartFilterOpen] = useState<boolean>(false);
  const [smartFilterApplied, setSmartFilterApplied] = useState<SmartFilterOptions>({
    adults: false,
    babies: false,
    children: false,
    juniors: false,
    seniors: false,
  });

  const getAccommodations = useCallback(async () => {
    const request = new GetAccommodationsRequest();
    const response = await getAccomm(request);

    setAccommodations(response.getAccommodationList().map(e => e.toObject()));
  }, [getAccomm, setAccommodations]);

  const getMealplans = useCallback(async () => {
    const request = new GetAllMealPlansRequest();
    request.setPropertyTicker(propertyTicker);
    const response = await mealplansClient.getAllMealPlans(request);

    setMealplans(response.getMealPlansList().map(m => m.toObject()));
  }, [propertyTicker, mealplansClient]);

  const getOccupations = useCallback(async () => {
    const request = new GetAllConfigurationRequest();
    const response = await getOccupationsCall(request);
    setOccupations(response.getConfigurationsList().map(m => m.toObject()));
  }, [getOccupationsCall]);

  const getConditions = useCallback(async () => {
    // TODO: replace with httpclient
    const response = await axios.get(`${BASE_URL}/api/backoffice/${propertyTicker}/conditions`);

    setConditions(response?.data);
  }, [propertyTicker]);

  useEffect(() => {
    getAccommodations();
    getMealplans();
    getConditions();
    getOccupations();
  }, [getAccommodations, getMealplans, getConditions, getOccupations]);

  useEffect(
    function populateMultiCheckboxListIfAllCallsFetched() {
      if (!originalOptions) {
        if (accommodations && mealPlans && conditions && occupations) {
          const optns: Options = {
            accommodations: undefined,
            occupations: undefined,
            mealPlans: undefined,
            conditions: undefined,
          };
          optns.accommodations = accommodations.map(it => ({
            ticker: it.ticker,
            translations: it.translationsList.map(translation => ({
              locale: translation.locale,
              name: translation.name,
            })),
          }));
          optns.occupations = occupations.map(it => ({
            ticker: it.ticker,
            translations: it.translationsList.map((translation: any) => ({
              locale: translation.locale,
              name: translation.name,
            })),
          }));
          optns.mealPlans = mealPlans.map(it => ({
            ticker: it.ticker,
            translations: it.translationsList.map(translation => ({
              locale: translation.locale,
              name: translation.name,
            })),
          }));
          optns.conditions = conditions.map(it => ({
            ticker: it.ticker,
            translations: it.translations.map((translation: any) => ({
              locale: translation.locale,
              name: translation.name,
            })),
          }));
          setOriginalOptions(optns);
          setOptions(optns);
          setFetchError(false);
        } else if (!fetchError) {
          setFetchError(true);
        }
      }
    },
    [originalOptions, accommodations, mealPlans, conditions, occupations, fetchError]
  );

  useEffect(
    function filterOccupationsBySelectedGuestOptions() {
      if (Object.values(smartFilterApplied).some(val => val)) {
        let occup = cloneDeep(occupations);
        const optns = cloneDeep(originalOptions);
        if (optns && occup) {
          if (
            smartFilterApplied.adults &&
            Object.values(smartFilterApplied).filter(val => val).length === 1
          ) {
            occup = occup.filter(
              occ => occ.adults && !occ.seniors && !occ.babies && !occ.children && !occ.juniors
            );
          }
          if (
            smartFilterApplied.seniors &&
            Object.values(smartFilterApplied).filter(val => val).length === 1
          ) {
            occup = occup.filter(
              occ => occ.seniors && !occ.adults && !occ.babies && !occ.children && !occ.juniors
            );
          }
          if (
            smartFilterApplied.adults &&
            smartFilterApplied.seniors &&
            Object.values(smartFilterApplied).filter(val => val).length === 2
          ) {
            occup = occup.filter(
              occ => occ.adults && occ.seniors && !occ.babies && !occ.children && !occ.juniors
            );
          }
          if (
            smartFilterApplied.babies &&
            !smartFilterApplied.children &&
            !smartFilterApplied.juniors
          ) {
            occup = occup.filter(occ => !occ.children && !occ.juniors);
          }
          if (
            smartFilterApplied.babies &&
            smartFilterApplied.children &&
            !smartFilterApplied.juniors
          ) {
            occup = occup.filter(occ => !occ.juniors);
          }
          if (
            smartFilterApplied.babies &&
            smartFilterApplied.juniors &&
            !smartFilterApplied.children
          ) {
            occup = occup.filter(occ => !occ.children);
          }
          if (
            smartFilterApplied.children &&
            !smartFilterApplied.babies &&
            !smartFilterApplied.juniors
          ) {
            occup = occup.filter(occ => !occ.babies && !occ.juniors);
          }
          if (
            smartFilterApplied.children &&
            smartFilterApplied.juniors &&
            !smartFilterApplied.babies
          ) {
            occup = occup.filter(occ => !occ.babies);
          }
          if (
            smartFilterApplied.juniors &&
            !smartFilterApplied.babies &&
            !smartFilterApplied.children
          ) {
            occup = occup.filter(occ => !occ.babies && !occ.children);
          }
          if (
            !smartFilterApplied.adults &&
            (smartFilterApplied.juniors || smartFilterApplied.babies || smartFilterApplied.children)
          ) {
            occup = occup.filter(occ => occ.adults && (occ.babies || occ.juniors || occ.children));
          }

          optns.occupations = occup.map(it => ({
            ticker: it.ticker,
            translations: it.translationsList.map((translation: any) => ({
              locale: translation.locale,
              name: translation.name,
            })),
          }));
          setOptions(optns);
        }
      } else {
        setOptions(originalOptions);
      }
    },
    [smartFilterApplied, originalOptions, occupations]
  );

  const onUpdate = useCallback(async () => {
    const request = new CreateInventoryLinesRequest();
    request.setAccommodationsList(values.accommodations);
    request.setConfigurationsList(values.occupations);
    request.setMealPlansList(values.mealPlans);
    request.setConditionsList(values.conditions);
    setLoading(true);
    try {
      const response = await createInventoryLinesCall(request);
      const responseObj = response.toObject();
      const formattedResponse = {
        createdInventories: responseObj.createdInventories,
        deletedInventories: 0,
        updatedInventories: 0,
        unchangedInventories: 0,
        createdVariables: responseObj.createdTickersList,
        errors: responseObj.errorsList.map(error => {
          return {
            ticker: error.ticker,
            relatedTickersList: error.relatedTickersList,
            valueType: Object.keys(InventoryLineValueType)[error.valueType],
            errorCode: Object.keys(InventoryLineErrorCode)[error.errorCode],
            errorMessage: error.errorMessage,
          };
        }),
      };
      setResult(formattedResponse);
    } catch (err) {
      //@ts-ignore
      setError(err);
    }
    setLoading(false);
  }, [values, setResult, createInventoryLinesCall]);

  const onChange = useCallback(
    val => {
      const vls = cloneDeep(values);

      vls[val.field] = val.value;

      setValues(vls);
    },
    [values]
  );

  const onFiltersChange = useCallback(
    (type: string, key: string, vals: Options) => {
      const vls = cloneDeep(values);
      if (type === "select") {
        vls[key] = Array.isArray(vals) && vals.map(it => it.ticker);
      } else {
        vls[key] = [];
      }
      setValues(vls);
    },
    [values, setValues]
  );

  const handleSmartFilterClick = useCallback(() => {
    setSmartFilterOpen(!smartFilterOpen);
  }, [smartFilterOpen, setSmartFilterOpen]);

  const handleFilterChange = useCallback(
    (event, guestType) => {
      const applied = { ...smartFilterApplied };
      applied[guestType as keyof SmartFilterOptions] = event.target.checked;
      setSmartFilterApplied(applied);
    },
    [smartFilterApplied, setSmartFilterApplied]
  );

  const smartFilter = useMemo(
    () => (
      <StyledCollapseContainer open={smartFilterOpen}>
        <CollapseHeader onClick={handleSmartFilterClick}>
          <FormattedMessage {...messages.guests} /> <StyledArrowDown flip={smartFilterOpen} />{" "}
        </CollapseHeader>
        <CollapseBody open={smartFilterOpen}>
          <CollapseListWrapper>
            {Object.keys(smartFilterApplied).map(guestTypeRaw => {
              const guestType: keyof SmartFilterOptions = guestTypeRaw as keyof SmartFilterOptions;
              return (
                <FormGroup className="form-inline">
                  <div className="checkbox c-checkbox needsclick">
                    <label className="needsclick ">
                      <input
                        type="checkbox"
                        checked={smartFilterApplied[guestType as keyof SmartFilterOptions]}
                        className="needsclick"
                        onChange={event => handleFilterChange(event, guestType)}
                      />
                      <em className="fa fa-check" />
                      <FormattedMessage {...messages[guestType]} />
                    </label>
                  </div>
                </FormGroup>
              );
            })}
          </CollapseListWrapper>
        </CollapseBody>
      </StyledCollapseContainer>
    ),
    [smartFilterOpen, handleSmartFilterClick, smartFilterApplied, handleFilterChange]
  );

  const optionsTable = useMemo(
    () =>
      options &&
      Object.entries(options).map(([keyRaw, vals], idx) => {
        const key: keyof Options = keyRaw as keyof Options;
        return (
          <div>
            <MessageContainer>
              {messages[key] && <FormattedMessage {...messages[key]} />}
            </MessageContainer>
            <CheckAllControlFilter marginBottom={idx === 1}>
              <span onClick={() => onFiltersChange("select", key, vals)}>
                <FormattedMessage {...messages.selectAll} />
              </span>
              <span>{" | "}</span>
              <span onClick={() => onFiltersChange("deselect", key, vals)}>
                {" "}
                <FormattedMessage {...messages.selectNone} />
              </span>
            </CheckAllControlFilter>
            {key === "occupations" && (
              <>
                <Placeholder />
                {smartFilter}
              </>
            )}
            <StyledMultiCheckBoxGroup
              value={values && values[key]}
              field={{
                fieldName: key,
                options:
                  Array.isArray(vals) &&
                  vals.map(obj => {
                    return {
                      ticker: obj.ticker,
                      name:
                        obj.translations.find(
                          (it: { locale: string; name: string }) => it.locale === locale
                        )?.name || obj.translations[0].name,
                    };
                  }),
              }}
              onChange={onChange}
              restrictedHeight={key === "occupations"}
            />
          </div>
        );
      }),
    [options, values, smartFilter, locale, onChange, onFiltersChange]
  );

  if (isLoading) {
    return <FullWidthLoader />;
  }

  if (fetchError) {
    return <FormattedMessage {...messages.fetchError} />;
  }
  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <OptionsTableContainer className="witadmin-bs3 witadmin-bs3-parent">
          {optionsTable}
        </OptionsTableContainer>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={onUpdate}>
            <FormattedMessage {...messages.create} />
          </Button>
        </ButtonContainer>
        {result && (
          <ResultContainer>
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.saveSuccess} />
              </strong>
            </Alert>
            <InventoryResultRenderer inventoryResult={result} />
          </ResultContainer>
        )}
      </Card>
    </>
  );
}

export default BatchIventory;
